import React, { Fragment, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { css } from '@emotion/css'
import {
  theme,
  IconKey
} from '@contrast-security-inc/design-system-foundations'
import { Text } from '@contrast-security-inc/design-system-typography'
import { FormValidator } from '../../legacy-design-system/form'
import {
  detailRendererContainerCss,
  PolicyManagementFormHeader
} from '../legacy-policy-panel/policy-management-form-header'
import { updateRoute } from '../route'
import { SensitiveDataTypeDefaults } from './sensitive-data-type-defaults'
import { CustomKeywordsForm } from './custom-keywords-form'
import Spinner from 'react-spinkit'
import { SensitiveDataPolicy } from '.'
import {
  LegacyPolicyPanel,
  LegacyPolicyPanelBody
} from '../legacy-policy-panel'
import { sendDefaultEvent } from '../analytics'
import { Button } from '@contrast-security-inc/design-system-buttons'

interface Props {
  readonly organizationID: string
  readonly policyID: string
  readonly loading: boolean
  readonly policy?: SensitiveDataPolicy
  readonly update: (props: {
    readonly organizationID: string
    readonly policyID: string
    readonly rules: Array<{
      readonly keywords: string[]
    }>
  }) => Promise<any>
}

interface FormLoadingProps {
  readonly onClose: () => void
}

interface FormRealProps {
  readonly organizationID: string
  readonly onClose: () => void
  readonly policyID: string
  readonly policy: SensitiveDataPolicy
  readonly update: (props: {
    readonly organizationID: string
    readonly policyID: string
    readonly rules: Array<{
      readonly keywords: string[]
    }>
  }) => Promise<any>
}

export const routeToSensitiveDataGrid = (organizationID: string) =>
  updateRoute(`/${organizationID}/rules/sensitive`)

const buildUpdateFnData = ({
  organizationID,
  policyID,
  rules
}: {
  readonly organizationID: string
  readonly policyID: string
  readonly rules: Array<{
    // ID is optional because the rule may be new
    readonly keywords: string[]
  }>
}) => {
  return {
    organizationID,
    policyID,
    rules: rules.map(({ ...rest }) => {
      return {
        ...rest
      }
    })
  }
}

const initCustomKeywordsState = (policy?: SensitiveDataPolicy) => {
  if (!policy) {
    return []
  }

  return policy.customKeywords
}

export function EditSensitiveDataTypeForm({
  organizationID,
  loading,
  policyID,
  policy,
  update
}: Props) {
  sendDefaultEvent('sensitive.data.edit.form.view')
  if (loading) {
    return (
      <FormLoading onClose={() => routeToSensitiveDataGrid(organizationID)} />
    )
  }
  if (!policy) {
    throw new Error('policy is required')
  }

  return (
    <FormReal
      organizationID={organizationID}
      onClose={() => routeToSensitiveDataGrid(organizationID)}
      policyID={policyID}
      policy={policy}
      update={update}
    />
  )
}

function FormLoading({ onClose }: FormLoadingProps) {
  return (
    <LegacyPolicyPanel>
      <LegacyPolicyPanelBody>
        <Header onClose={onClose} />
        <Spinner
          data-testid="sensitive-data-grid-loading-indicator"
          className={css`
            text-align: center;
          `}
          name="ball-beat"
          fadeIn="none"
          color={theme.color.teal50}
        />
      </LegacyPolicyPanelBody>
    </LegacyPolicyPanel>
  )
}

function FormReal({
  organizationID,
  onClose,
  policyID,
  policy,
  update
}: FormRealProps) {
  const [customKeywords, setCustomKeywords] = useState(
    initCustomKeywordsState(policy)
  )

  const [formHasChanged, setFormHasChanged] = useState(false)

  const formValue = {
    customKeywords
  }

  return (
    <FormValidator value={formValue}>
      {({ formIsValid }) => {
        return (
          <Fragment>
            <LegacyPolicyPanel
              children={
                <LegacyPolicyPanelBody>
                  <React.Fragment>
                    <Header onClose={onClose} />
                    <div
                      className={css`
                        margin-left: 10px;
                      `}
                    >
                      <SensitiveDataTypeDefaults policy={policy} />
                      <CustomKeywordsForm
                        customKeywords={customKeywords}
                        onKeywordsChange={(keywords) => {
                          setCustomKeywords(keywords)
                          setFormHasChanged(true)
                        }}
                      />
                    </div>
                  </React.Fragment>
                </LegacyPolicyPanelBody>
              }
              outerFooter={
                <Actions
                  organizationID={organizationID}
                  policyID={policyID}
                  customKeywords={customKeywords}
                  update={update}
                  formIsValid={formIsValid}
                  formHasChanged={formHasChanged}
                />
              }
            />
          </Fragment>
        )
      }}
    </FormValidator>
  )
}

function Header({ onClose }: { readonly onClose: () => void }) {
  const intl = useIntl()

  return (
    <PolicyManagementFormHeader
      i18nID="action_sensitiveDataType_edit"
      Icon={IconKey}
      detailRenderer={() => (
        <div className={detailRendererContainerCss}>
          <Text font="body" color="neutral60">
            {intl.formatMessage({ id: 'detail_sensitiveData_edit' })}
          </Text>
        </div>
      )}
      onClose={() => {
        onClose()
        sendDefaultEvent('sensitive.data.edit.form.leave.close')
      }}
    />
  )
}

function Actions({
  organizationID,
  policyID,
  customKeywords,
  update,
  formIsValid,
  formHasChanged
}: {
  readonly organizationID: string
  readonly policyID: string
  readonly update: (props) => Promise<any>
  readonly formIsValid: boolean
  readonly formHasChanged: boolean
  readonly customKeywords: string[]
}) {
  return (
    <FormattedMessage
      id={'detail_sensitiveDataType_saveSuccessMessage'}
      children={() => (
        <FormattedMessage
          id={'detail_sensitiveDataType_saveErrorMessage'}
          children={() => (
            <FormattedMessage
              id={'action_save'}
              children={(saveMessage) => (
                <FormattedMessage
                  id="action_cancel"
                  children={(cancelMessage) => (
                    <Fragment>
                      <Button
                        data-testid="sensitive-data-form-update-btn"
                        className={css`
                          margin-right: 20px;
                        `}
                        onClick={async () => {
                          const mapped = buildUpdateFnData({
                            organizationID,
                            policyID,
                            rules: [{ keywords: customKeywords }]
                          })
                          await update(mapped)
                          sendDefaultEvent('sensitive.data.edit.form.update')
                        }}
                        isDisabled={!formIsValid || !formHasChanged}
                      >
                        {saveMessage}
                      </Button>
                      <Button
                        variant="secondary"
                        className={css`
                          margin-right: 7px;
                        `}
                        onClick={() => {
                          routeToSensitiveDataGrid(organizationID)
                          sendDefaultEvent(
                            'sensitive.data.edit.form.leave.cancel'
                          )
                        }}
                      >
                        {cancelMessage}
                      </Button>
                    </Fragment>
                  )}
                />
              )}
            />
          )}
        />
      )}
    />
  )
}
